import {
  Archive,
  CalendarRange,
  Check,
  FilePenLine,
  History,
  Plus,
  Quote,
  ScanText,
  ShieldCheck,
  Sparkles,
  Trash2
} from "lucide-react";
import { useState } from "react";
import { MarkdownContent } from "../components/MarkdownContent";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { formatDate } from "../lib/format";
import { getRollingReviewPeriod } from "../../shared/reviewPeriod";

function getSourceCount(raw: string) {
  try {
    const value = JSON.parse(raw) as unknown;
    return Array.isArray(value) ? value.length : 0;
  } catch {
    return 0;
  }
}

export function ReviewsPage() {
  const { data, mutate, openModal } = useApp();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  if (!data) return null;

  const currentPeriod = getRollingReviewPeriod(
    new Date(),
    data.profile?.timezone ?? "Asia/Shanghai"
  );
  const currentReview = data.reviews.find(
    (review) =>
      review.period_start === currentPeriod.start && review.period_end === currentPeriod.end
  );
  const selected = selectedId
    ? data.reviews.find((review) => review.id === selectedId) ?? currentReview ?? data.reviews[0]
    : currentReview ?? data.reviews[0];
  const publishedCount = data.reviews.filter((review) => review.status === "PUBLISHED").length;
  const draftCount = data.reviews.length - publishedCount;

  const generate = async () => {
    if (currentReview) {
      setSelectedId(currentReview.id);
      return;
    }
    const result = await mutate(() => api.generateReview(), "本周复盘草稿已生成");
    if (result) setSelectedId(result.id);
  };

  const publish = (id: string) =>
    mutate(() => api.publishReview(id), "复盘已发布并保留当前版本");

  return (
    <div className="page page--reviews review-room">
      <header className="review-masthead">
        <div className="review-masthead__identity">
          <span className="review-masthead__mark"><ScanText size={22} /></span>
          <div>
            <span className="review-kicker">AFTER ACTION ARCHIVE</span>
            <h1>复盘中心</h1>
            <p>回看事实，留下判断。</p>
          </div>
        </div>
        <button className="review-primary" type="button" onClick={() => void generate()}>
          <Sparkles size={17} />
          <span>
            {currentReview
              ? currentReview.status === "DRAFT"
                ? "审阅本周草稿"
                : "查看本周复盘"
              : "生成本周草稿"}
          </span>
        </button>
      </header>

      <section className="review-cycle" aria-label="当前复盘周期">
        <div className="review-cycle__period">
          <span><CalendarRange size={20} /></span>
          <div>
            <small>当前滚动周期</small>
            <strong>{formatDate(currentPeriod.start)} — {formatDate(currentPeriod.end)}</strong>
            <p>只把已经发生的事带进本轮判断。</p>
          </div>
        </div>
        <div className="review-cycle__metrics" aria-label="复盘档案统计">
          <div><strong>{data.events.length}</strong><span>事实记录</span></div>
          <div><strong>{publishedCount}</strong><span>已发布</span></div>
          <div><strong>{draftCount}</strong><span>待审阅</span></div>
        </div>
      </section>

      <main className="review-chronicle">
        <aside className="review-archive" aria-label="复盘档案">
          <header className="review-archive__head">
            <div>
              <span><Archive size={17} /></span>
              <div><h2>档案轴</h2><p>{data.reviews.length} 个真实版本</p></div>
            </div>
            <History size={17} aria-hidden="true" />
          </header>

          {data.reviews.length ? (
            <nav className="review-archive__list" aria-label="选择一份复盘">
              {data.reviews.map((review, index) => {
                const active = selected?.id === review.id;
                return (
                  <div className={`review-entry${active ? " is-active" : ""}`} key={review.id}>
                    <button
                      className="review-entry__select"
                      type="button"
                      aria-current={active ? "page" : undefined}
                      onClick={() => setSelectedId(review.id)}
                    >
                      <span className="review-entry__index">{String(index + 1).padStart(2, "0")}</span>
                      <span className="review-entry__body">
                        <strong>{review.title}</strong>
                        <small>{formatDate(review.created_at)} · {getSourceCount(review.source_event_ids)} 条事实</small>
                      </span>
                      <span className={`review-status review-status--${review.status.toLowerCase()}`}>
                        {review.status === "DRAFT" ? "草稿" : "已发布"}
                      </span>
                    </button>
                    <button
                      className="review-entry__delete"
                      type="button"
                      aria-label={`删除复盘：${review.title}`}
                      onClick={() => {
                        if (selectedId === review.id) setSelectedId(null);
                        void mutate(() => api.remove("reviews", review.id), "复盘已移入回收站");
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </nav>
          ) : (
            <div className="review-archive__empty">
              <FilePenLine size={22} />
              <strong>档案轴还是空的</strong>
              <p>生成第一份复盘后，每次判断都会在这里保留版本。</p>
            </div>
          )}
        </aside>

        <article className="review-dossier" aria-live="polite">
          {selected ? (
            <>
              <header className="review-dossier__head">
                <div>
                  <span className="review-kicker">{selected.review_type} REVIEW · {formatDate(selected.created_at)}</span>
                  <h2>{selected.title}</h2>
                  <p>事实区间 {formatDate(selected.period_start)} — {formatDate(selected.period_end)}</p>
                </div>
                <span className={`review-dossier__seal review-dossier__seal--${selected.status.toLowerCase()}`}>
                  {selected.status === "DRAFT" ? "等待裁定" : "正式归档"}
                </span>
              </header>

              <div className="review-dossier__ledger" aria-label="复盘元数据">
                <div><span>事实依据</span><strong>{getSourceCount(selected.source_event_ids)} 条</strong></div>
                <div><span>文档状态</span><strong>{selected.status === "DRAFT" ? "草稿" : "已发布"}</strong></div>
                <div><span>生成日期</span><strong>{formatDate(selected.created_at)}</strong></div>
              </div>

              <section className="review-dossier__narrative" aria-label="复盘正文">
                <MarkdownContent content={selected.content} />
              </section>

              <footer className="review-dossier__foot">
                <div className="review-provenance">
                  <Quote size={17} />
                  <span>本复盘引用 {getSourceCount(selected.source_event_ids)} 条事实记录，不用外部推测补齐空白。</span>
                </div>
                {selected.status === "DRAFT" ? (
                  <div className="review-dossier__actions">
                    <button className="review-secondary" type="button" onClick={() => openModal("quick")}>
                      <Plus size={16} /><span>补充事实</span>
                    </button>
                    <button className="review-primary" type="button" onClick={() => void publish(selected.id)}>
                      <Check size={16} /><span>确认并发布</span>
                    </button>
                  </div>
                ) : (
                  <div className="review-dossier__archived"><ShieldCheck size={16} /> 此版本已归档</div>
                )}
              </footer>
            </>
          ) : (
            <div className="review-dossier__empty">
              <span><FilePenLine size={28} /></span>
              <div>
                <span className="review-kicker">EMPTY ARCHIVE</span>
                <h2>从第一份真实复盘开始</h2>
                <p>
                  {data.events.length
                    ? "系统已经有事实可以回看。生成草稿后，你再决定哪些判断值得留下。"
                    : "先记录一件真实发生的事，复盘才有判断的依据。"}
                </p>
              </div>
              <button
                className="review-primary"
                type="button"
                onClick={data.events.length ? () => void generate() : () => openModal("quick")}
              >
                {data.events.length ? <Sparkles size={16} /> : <Plus size={16} />}
                <span>{data.events.length ? "生成本周草稿" : "记录一条事实"}</span>
              </button>
            </div>
          )}
        </article>
      </main>
    </div>
  );
}
